/**
 * Module Access Section - Reusable component for configuring enabled modules.
 * Used in both TenantSettingsPage (sysadmin/admin) and UserForm (admin).
 */
import { useTranslation } from 'react-i18next';
import { TOGGLEABLE_MODULE_IDS, getModuleById } from '../../config/modules.config';
import { useModuleName, useModuleDescription } from '../../hooks/useModuleName';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../components/ui/tooltip';

export interface ModuleAccessSectionProps {
  /** Currently enabled module IDs. NULL means all enabled. */
  enabledModules: string[] | null;
  /** Callback when enabled modules change */
  onChange: (modules: string[] | null) => void;
  /** Module IDs that are available to select from (for user form, limited by tenant) */
  availableModules?: string[];
  /** Module ceiling — modules above ceiling are hidden entirely. NULL means no ceiling. */
  ceilingModules?: string[] | null;
  /** Whether the section is read-only */
  readOnly?: boolean;
  /** Show "all enabled" toggle at top */
  showAllToggle?: boolean;
  /** Show all toggleable modules including unavailable ones (greyed out) */
  showAllModules?: boolean;
}

export function ModuleAccessSection({
  enabledModules,
  onChange,
  availableModules,
  ceilingModules,
  readOnly = false,
  showAllToggle = true,
  showAllModules = false,
}: ModuleAccessSectionProps) {
  const { t } = useTranslation(['admin', 'common']);
  const getModuleName = useModuleName();
  const getModuleDescription = useModuleDescription();

  // When ceiling is set, filter to only ceiling modules (hide above-ceiling entirely)
  // When showAllModules is true, show all toggleable modules (for user settings page)
  // Otherwise, use availableModules or all toggleable modules
  let moduleIds = showAllModules
    ? [...TOGGLEABLE_MODULE_IDS]
    : (availableModules ?? [...TOGGLEABLE_MODULE_IDS]);

  // Apply ceiling filter — hide modules above ceiling
  if (ceilingModules !== undefined && ceilingModules !== null) {
    const ceilingSet = new Set(ceilingModules);
    moduleIds = moduleIds.filter(id => ceilingSet.has(id));
  }

  // Check if all modules are enabled
  const allEnabled = enabledModules === null;

  // Get module configs for display
  const modules = moduleIds
    .map(id => getModuleById(id))
    .filter((m): m is NonNullable<typeof m> => m !== undefined);

  const handleAllToggle = () => {
    if (allEnabled) {
      // Switch to explicit list with all modules enabled
      onChange([...moduleIds]);
    } else {
      // Switch back to null (all enabled)
      onChange(null);
    }
  };

  const handleModuleToggle = (moduleId: string) => {
    if (readOnly) return;

    if (allEnabled) {
      // First toggle when "all enabled" - disable this one module
      const newList = moduleIds.filter(id => id !== moduleId);
      onChange(newList);
    } else {
      const isEnabled = enabledModules?.includes(moduleId) ?? false;
      if (isEnabled) {
        // Remove from list
        const newList = enabledModules?.filter(id => id !== moduleId) ?? [];
        onChange(newList);
      } else {
        // Add to list
        const newList = [...(enabledModules ?? []), moduleId];
        onChange(newList);
      }
    }
  };

  const isModuleChecked = (moduleId: string): boolean => {
    if (allEnabled) return true;
    return enabledModules?.includes(moduleId) ?? false;
  };

  const isModuleAvailable = (moduleId: string): boolean => {
    if (!availableModules) return true;
    return availableModules.includes(moduleId);
  };

  // Note: We used to have indeterminate state for partial selection,
  // but with the new "all modules" greyed-out behavior, it's no longer needed.

  return (
    <div>
      {showAllToggle && (
        <div
          role="checkbox"
          aria-checked={allEnabled}
          tabIndex={readOnly ? -1 : 0}
          className={`flex items-center gap-2 mb-4 ${readOnly ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
          onClick={() => !readOnly && handleAllToggle()}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && !readOnly) {
              e.preventDefault();
              handleAllToggle();
            }
          }}
        >
          {/* Visual-only checkbox */}
          <div
            className={`
              h-4 w-4 shrink-0 rounded-sm border border-primary
              flex items-center justify-center
              ${readOnly ? 'opacity-50' : ''}
            `}
            style={{
              backgroundColor: allEnabled ? 'hsl(var(--primary))' : 'transparent',
            }}
            aria-hidden="true"
          >
            {allEnabled && (
              <svg
                className="h-3 w-3 text-primary-foreground"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            )}
          </div>
          <span className="font-medium">
            {t('admin:moduleAccess.allAvailableModules')}
          </span>
        </div>
      )}

        <TooltipProvider delayDuration={300}>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-2">
            {modules.map(module => {
              const isAvailable = isModuleAvailable(module.id);
              const isChecked = isModuleChecked(module.id);
              // Disable individual checkboxes when "all modules" is selected (null = all enabled)
              const isDisabled = readOnly || !isAvailable || allEnabled;

              // Get the module description for tooltip
              const moduleDescription = getModuleDescription(module);

              // Build tooltip content
              const tooltipContent = !isAvailable ? (
                <div className="max-w-xs">
                  <p className="font-medium text-destructive mb-1">
                    {t('admin:moduleAccess.notSubscribed')}
                  </p>
                  <p className="text-muted-foreground">{moduleDescription}</p>
                </div>
              ) : (
                <p className="max-w-xs">{moduleDescription}</p>
              );

              // Use div wrapper with onClick, but DON'T use label to avoid double-triggering
              // Radix Checkbox uses a button internally, so wrapping in label can cause issues
              const checkboxContent = (
                <div
                  role="checkbox"
                  aria-checked={isChecked}
                  tabIndex={isDisabled ? -1 : 0}
                  className={`
                    flex items-center gap-2 p-2 rounded-md
                    ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}
                    ${!isAvailable ? 'opacity-40' : isDisabled ? 'opacity-50' : ''}
                    ${isChecked && isAvailable && !allEnabled ? 'bg-opacity-10' : ''}
                    ${isAvailable && !allEnabled ? 'hover:bg-muted' : ''}
                  `}
                  style={{
                    backgroundColor: isChecked && isAvailable && !allEnabled ? `${module.color}14` : undefined,
                  }}
                  onClick={() => {
                    if (!isDisabled) handleModuleToggle(module.id);
                  }}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && !isDisabled) {
                      e.preventDefault();
                      handleModuleToggle(module.id);
                    }
                  }}
                >
                  {/* Visual-only checkbox - click handling is on parent div */}
                  <div
                    className={`
                      h-4 w-4 shrink-0 rounded-sm border
                      flex items-center justify-center
                    `}
                    style={{
                      borderColor: !isAvailable ? 'hsl(var(--muted-foreground))' : (isChecked && !allEnabled ? module.color : 'hsl(var(--primary))'),
                      backgroundColor: isChecked && isAvailable ? (isChecked && !allEnabled ? module.color : 'hsl(var(--primary))') : 'transparent',
                    }}
                    aria-hidden="true"
                  >
                    {isChecked && isAvailable && (
                      <svg
                        className="h-3 w-3 text-primary-foreground"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className="flex items-center"
                      style={{ color: isAvailable && !allEnabled ? module.color : undefined }}
                    >
                      {module.icon}
                    </span>
                    <span
                      className={`${!isAvailable || allEnabled ? 'text-muted-foreground' : ''}`}
                    >
                      {getModuleName(module)}
                    </span>
                  </div>
                </div>
              );

              return (
                <Tooltip key={module.id}>
                  <TooltipTrigger asChild>
                    <div>{checkboxContent}</div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    {tooltipContent}
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </div>
        </TooltipProvider>
    </div>
  );
}
